import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import useOutsideClick from "../../hooks/useOutsideClick";
import Spinner from "../Spinner";
import { FormLabel } from "../Input/FormLabel";
import {
  buildFullValue,
  filterOptions,
  findSelectedOption,
  getDisplayValueFromOption,
  getOptionLabel,
  isNestedCategory,
  normalizeSelection,
} from "./select.utils";

/**
 * Modern combobox-style Select with search, nested categories, and custom
 * dropdown rendering. Matches legacy onChange payload shape for compatibility.
 */
export const Select = (props) => {
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [categoryStack, setCategoryStack] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [showError, setShowError] = useState(false);

  const displayValueFrom = props.displayValueFrom || "label";
  const options = props.options || [];

  const selectedOption = useMemo(
    () => findSelectedOption(options, props.value, { displayValueFrom }),
    [options, props.value, displayValueFrom],
  );

  const selectedLabel = useMemo(() => {
    if (selectedOption) {
      return buildFullValue(selectedOption, []);
    }

    if (props.value == null || props.value === "") {
      return "";
    }

    if (typeof props.value === "string" || typeof props.value === "number") {
      return String(props.value);
    }

    return getOptionLabel(props.value);
  }, [selectedOption, props.value]);

  const currentOptions = categoryStack.length
    ? categoryStack[categoryStack.length - 1].options || []
    : options;

  const filteredOptions = useMemo(() => {
    if (!props.search || !query.trim()) {
      return currentOptions;
    }

    if (props.fullTextSearch) {
      return currentOptions;
    }

    return filterOptions(currentOptions, query, props.searchKeys || []);
  }, [currentOptions, props.search, props.fullTextSearch, props.searchKeys, query]);

  const visibleOptions = useMemo(() => {
    if (
      props.search &&
      query.trim() &&
      filteredOptions.length === 0 &&
      !props.fullTextSearch
    ) {
      const emptyLabel =
        typeof props.emptyLabel === "function"
          ? props.emptyLabel(query)
          : props.emptyLabel || "No items match this search";

      return [
        {
          label: emptyLabel,
          value: "EMPTY_VALUE",
          disabled: true,
        },
      ];
    }

    return filteredOptions;
  }, [filteredOptions, props.emptyLabel, props.fullTextSearch, props.search, query]);

  useEffect(() => {
    if (props.fullTextSearch && props.search) {
      props.fullTextSearch(query);
    }
  }, [query, props.fullTextSearch, props.search]);

  useEffect(() => {
    setActiveIndex(visibleOptions.length > 0 ? 0 : -1);
  }, [visibleOptions.length, isOpen, categoryStack.length]);

  useEffect(() => {
    if (!isOpen) {
      setCategoryStack([]);
      setQuery("");
    }
  }, [isOpen]);

  useOutsideClick(containerRef, () => {
    if (isOpen) {
      closePicker();
    }
  });

  const sizeLg = props.size === "lg";
  const sizeMd = props.size === "md" || !props.size;
  const sizeSm = props.size === "sm";

  const labelClasses = classNames("block mb-1", "text-sm font-medium", {
    "text-gray-F900": !showError && !props.loading,
    "text-red-700": props.error && showError,
  });

  const inputClasses = classNames(
    props.className,
    "shadow-sm bg-white",
    "block w-full",
    "border rounded-lg",
    "pr-10",
    {
      "cursor-text": props.search,
      "p-4 sm:text-md": sizeLg,
      "p-2.5 text-sm": sizeMd,
      "p-2 sm:text-xs": sizeSm,
      "bg-gray-100 cursor-not-allowed focus:ring-gray-300":
        props.loading || props.disabled,
      "border-gray-300 focus:outline-none focus-visible:ring-1 focus:ring-1 focus-visible:ring-primary-500 focus:ring-primary-500 focus:border-primary-500 text-gray-900":
        !showError && !props.loading && !props.disabled,
      "bg-red-50 border-red-500 text-red-900 placeholder-red-700 focus:outline-2 focus:ring-red-500 focus:border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500":
        props.error && showError,
    },
  );

  const optionClasses = classNames(
    "cursor-pointer select-none",
    { "p-4 sm:text-md": sizeLg },
    { "p-2.5 text-sm": sizeMd },
    { "px-2 py-1 sm:text-xs": sizeSm },
  );

  const wrapperClasses = classNames("block", props.wrapperClasses);
  const ChevronIcon = isOpen ? ChevronUpIcon : ChevronDownIcon;
  const isDisabled = Boolean(props.loading || props.disabled);
  const displayValue =
    isOpen && props.search ? query : selectedLabel;

  function closePicker() {
    setIsOpen(false);
    setQuery("");
    setCategoryStack([]);
    setActiveIndex(-1);
  }

  /**
   * Close when focus leaves the field, including Tab to the next control.
   * Menu rows cancel mousedown so choosing an option does not count as leaving.
   *
   * @param {React.FocusEvent<HTMLElement>} event
   */
  function handleContainerBlur(event) {
    const next = event.relatedTarget;
    if (next && containerRef.current?.contains(next)) return;
    closePicker();
  }

  function openPicker() {
    if (isDisabled) return;
    setIsOpen(true);
  }

  function emitChange(rawSelection) {
    const option = normalizeSelection(rawSelection);
    if (!option) return;

    const payload = {
      ...option,
      data: option,
      fullValue: buildFullValue(option, categoryStack),
    };

    if (props.id) {
      payload.id = props.id;
    }

    props.onChange && props.onChange(payload);
  }

  function selectOption(option) {
    if (!option || option.disabled || option.value === "EMPTY_VALUE") {
      return;
    }

    if (isNestedCategory(option)) {
      setCategoryStack((prev) => [...prev, option]);
      setQuery("");
      setActiveIndex(0);
      return;
    }

    emitChange(option);

    if (!props.keepOpen) {
      closePicker();
      inputRef.current?.blur?.();
    }
  }

  function handleInputChange(event) {
    if (!props.search) return;

    const nextValue = event.target.value;
    setQuery(nextValue);
    openPicker();
  }

  function handleInputFocus() {
    if (isDisabled) return;
    openPicker();

    if (props.search && selectedLabel && !query) {
      setQuery(selectedLabel);
      setTimeout(() => inputRef.current?.select?.(), 0);
    }
  }

  function handleToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (isDisabled) return;

    if (isOpen) {
      closePicker();
    } else {
      openPicker();
      inputRef.current?.focus?.();
    }
  }

  function handleBlur() {
    if (props.error) {
      setTimeout(() => setShowError(true), 200);
    } else {
      setShowError(false);
    }
  }

  function handleKeyDown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      openPicker();
      setActiveIndex((index) =>
        visibleOptions.length === 0
          ? -1
          : Math.min(index + 1, visibleOptions.length - 1),
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? 0 : index - 1));
      return;
    }

    if (event.key === "Enter" && isOpen && activeIndex >= 0) {
      event.preventDefault();
      selectOption(visibleOptions[activeIndex]);
      return;
    }

    if (event.key === "Escape") {
      closePicker();
      inputRef.current?.blur?.();
    }
  }

  function renderDefaultOptions(list) {
    return (
      <>
        {categoryStack.length > 0 && (
          <button
            type="button"
            className="flex w-full items-center p-2 text-sm text-gray-700 hover:bg-gray-100"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setCategoryStack((prev) => prev.slice(0, -1));
              setQuery("");
            }}
          >
            <ChevronLeftIcon className="h-4 w-4 mr-2" />
            <span>Back to Main Categories</span>
          </button>
        )}
        {list.map((item, index) => {
          const isActive = index === activeIndex;
          const isSelected =
            selectedOption &&
            getOptionLabel(selectedOption) === getOptionLabel(item) &&
            selectedOption.value === item.value;

          return (
            <div
              key={`${getOptionLabel(item)}-${index}`}
              role="option"
              aria-selected={isSelected}
              className={classNames(optionClasses, "hover:bg-gray-100", {
                "bg-gray-100": isActive,
                "cursor-not-allowed opacity-50": item.disabled,
                "font-semibold text-primary-700": isSelected,
              })}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => selectOption(item)}
            >
              {item.body || item.label}
            </div>
          );
        })}
      </>
    );
  }

  function renderDropdownPanel() {
    if (props.renderDropdown) {
      return props.renderDropdown({
        options: visibleOptions,
        handleChange: (selection) => selectOption(normalizeSelection(selection)),
      });
    }

    return (
      <div
        className="shadow rounded-b max-h-56 overflow-y-auto text-sm bg-white absolute border border-t-0 left-0 right-0 z-50"
        onMouseDown={(event) => event.preventDefault()}
      >
        {renderDefaultOptions(visibleOptions)}
      </div>
    );
  }

  return (
    <div className={wrapperClasses}>
      {props.label && (
        <FormLabel
          htmlFor={props.id}
          className={labelClasses}
          required={props.required}
        >
          {props.label}
        </FormLabel>
      )}

      <div
        ref={containerRef}
        className="relative w-full"
        onBlur={handleContainerBlur}
      >
        <input
          ref={inputRef}
          type="text"
          id={props.id}
          className={inputClasses}
          placeholder={props.placeholder || "Select an option"}
          readOnly={!props.search}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onClick={() => !props.search && openPicker()}
          disabled={isDisabled}
          autoComplete={props.autoComplete || "off"}
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete={props.search ? "list" : "none"}
        />

        {props.loading ? (
          <div className="flex absolute inset-y-0 right-0 items-center px-3">
            <Spinner size="sm" />
          </div>
        ) : (
          <button
            type="button"
            aria-label={isOpen ? "Close options" : "Open options"}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleToggleClick}
            disabled={isDisabled}
            tabIndex={-1}
          >
            <ChevronIcon className="h-4 w-4" />
          </button>
        )}

        {isOpen && renderDropdownPanel()}
      </div>

      {props.error && showError && (
        <p className="mt-2 text-sm text-red-600">{props.error}</p>
      )}
    </div>
  );
};

export default Select;

export {
  buildFullValue,
  filterOptions,
  findSelectedOption,
  getDisplayValueFromOption,
  getOptionLabel,
  isNestedCategory,
  normalizeSelection,
} from "./select.utils";
